import { useTimeout } from '@mantine/hooks'
import type { QueryKey } from '@tanstack/react-query'
import { useState } from 'react'

import { globalEnv } from '../services/state/helpers/setupRootStore'

export type MutationBaseOpts<P, R> = {
  onSuccess?: (data: R, params: P) => void
  onSuccessNotification?: (data: { title: string; message: string }) => void
  onError?: (error: Error, params: P) => void
  onErrorNotification?: (data: { title: string; message: string }) => void
}

export function handleMutationError<P, R>(notificationTitle: string, mutationOpts?: MutationBaseOpts<P, R>) {
  return function (error: Error, params: P) {
    const e = error as Error & { response: { data: { errors: { message: string }[] } } }

    mutationOpts?.onErrorNotification?.({
      title: notificationTitle,
      message: e?.response?.data?.errors?.[0]?.message,
    })

    mutationOpts?.onError?.(error, params)
  }
}

export function handleMutationSuccess<P, R extends { message: string }>(
  notificationMessage: string,
  queryKeysForInvalidation: QueryKey[] = [],
  mutationOpts?: MutationBaseOpts<P, R>,
) {
  return function (data: R, params: P) {
    for (const queryKey of queryKeysForInvalidation) {
      void globalEnv.api.queryClient.invalidateQueries({ queryKey })
    }

    mutationOpts?.onSuccessNotification?.({
      title: data.message,
      message: notificationMessage,
    })

    mutationOpts?.onSuccess?.(data, params)
  }
}

export function useAreFormQueriesFetched(
  isFetched: Array<boolean | null | undefined>,
  timeout: number | undefined = undefined,
): boolean {
  const [uxTimeout, setUxTimeout] = useState(typeof timeout === 'number' ? false : true)
  useTimeout(() => setUxTimeout(true), timeout ?? 500, { autoInvoke: typeof timeout === 'number' })

  return uxTimeout && isFetched.filter(v => typeof v === 'boolean').every(f => f)
}
